import { useState, useEffect } from "react";
import {
    Network, Server, Clock, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from "lucide-react";
import { showError } from "../../../../utils/toast";
import baseapi from "../../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";


interface ArpEntry {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    ip_address: string;
    mac_address: string;

}

interface NvrArpTableProps {
    assetId: number;
    clientId: number;
}

export default function NvrArpTable({ assetId, clientId }: NvrArpTableProps) {
    const [data, setData] = useState<ArpEntry[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchArpData = async () => {
        if (!assetId) return;

        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            // Depending on your API, asset_id might be passed as a direct parameter or inside a query object
            const params = {
                query: JSON.stringify({ asset_id: assetId, client_id: clientId }),
                skip,
                limit
            };

            const response = await baseapi.get("/nvr_arp_table", { params });

            if (response.data.status) {
                setData(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            } else {
                showError(response.data.message || "Failed to load ARP table data");
                setData([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "An error occurred while fetching ARP data");
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArpData();
    }, [assetId, currentPage, limit]);

    return (
        <div className="w-full flex flex-col gap-3 animate-in fade-in duration-300 text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/20 p-1.5 rounded-lg border border-indigo-500/30">
                        <Network size={16} className="text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white tracking-wide">ARP Table</h2>
                        <p className="text-[10px] text-gray-200 uppercase tracking-widest">Network Device Discovery</p>
                    </div>
                </div>

                <div className="text-[10px] font-bold text-gray-200 bg-gray-800/50 px-3 py-1.5 rounded-lg border border-gray-700">
                    Total Records: <span className="text-white">{totalRecords}</span>
                </div>
            </div>

            {/* Table Container - h-fit ensures it only takes required height without empty whitespace */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl h-fit w-full">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800/80 border-b border-gray-700/50">
                            <tr>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Server size={14} className="text-cyan-400" />
                                        IP Address
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Network size={14} className="text-purple-400" />
                                        MAC Address
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <Clock size={14} className="text-emerald-400" />
                                        Last Updated
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-gray-200 uppercase tracking-[0.2em]">Scanning Network...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <Search size={24} className="text-gray-200" />
                                            <p className="text-[10px] font-bold text-gray-200 uppercase tracking-widest">No ARP entries found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-gray-800/40 transition-colors group">
                                        <td className="px-4 py-2.5">
                                            <span className="text-[11px] font-mono font-bold text-indigo-300">
                                                {entry.ip_address}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className="text-[11px] font-mono font-bold text-gray-300 bg-gray-800 px-2 py-0.5 rounded border border-gray-700">
                                                {entry.mac_address}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <span className="text-[10px] font-semibold text-gray-200">
                                                {new Date(entry.updated + "Z").toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {totalRecords > 0 && (
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-800/60 border-t border-gray-700">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[10px] font-medium text-gray-200 tracking-widest">
                                <span className="text-gray-300">
                                    {(currentPage - 1) * limit + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-300">
                                    {Math.min(currentPage * limit, totalRecords)}
                                </span>
                                <span className="mx-1 text-gray-600">/</span>
                                <span className="text-gray-300 font-bold">{totalRecords}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronsLeft size={14} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronLeft size={14} />
                            </button>
                            <div className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded text-[10px] font-bold mx-1">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronRight size={14} />
                            </button>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronsRight size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}